import { cn } from '@myosh/odin-components';
import { memo, useCallback, useMemo } from 'react';
import type { HorizontallyMoveableNodeType, MoveableNodeTypes } from '../../../../helpers/node-util';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { moveNode, selectShowControlsInSingleRow } from '../../../../redux/slices/diagram';
import ArrowLeftIcon from '../icons/arrow-left-icon.component';
import ArrowRightIcon from '../icons/arrow-right-icon.component';

export const HORIZONTAL_STEP = 1;

export const HorizontalMoveDirection = {
  LEFT: 0,
  RIGHT: 1,
} as const;
export type HorizontalMoveDirection = (typeof HorizontalMoveDirection)[keyof typeof HorizontalMoveDirection];

interface HorizontalMoveControlProps {
  id: string;
  type: HorizontallyMoveableNodeType;
  index: number;
  rowIndex: number;
  title: string;
  direction: HorizontalMoveDirection;
  disabled: boolean;
}

const HorizontalMoveControl = ({
  id,
  type,
  index,
  rowIndex,
  title,
  direction,
  disabled,
}: HorizontalMoveControlProps) => {
  const dispatch = useAppDispatch();
  const showControlsInSingleRowState = useAppSelector(selectShowControlsInSingleRow);

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (disabled) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      // depending on the node type, left/right movement is different in the diagram
      // for preventative-control-node, moving right visually decreases the index in the array, moving left visually increases it
      const indexStep = type === 'mitigating-control-node' ? HORIZONTAL_STEP : -HORIZONTAL_STEP;

      dispatch(
        moveNode({
          id,
          type: type as MoveableNodeTypes,
          currentIndex: index,
          targetIndex: direction === HorizontalMoveDirection.LEFT ? index - indexStep : index + indexStep,
          rowIndex,
        })
      );
    },
    [index, rowIndex, type, direction, disabled]
  );

  const icon = useMemo(
    () => (direction === HorizontalMoveDirection.LEFT ? <ArrowLeftIcon /> : <ArrowRightIcon />),
    [direction]
  );

  const dataCyMoveControl = useMemo(() => {
    const directionStr = direction === HorizontalMoveDirection.LEFT ? 'left' : 'right';
    return `horizontal-move-control-${directionStr}-${index}`;
  }, [direction, index]);

  return (
    <div
      data-cy={dataCyMoveControl}
      title={title}
      className={cn(
        'nopan nodrag invisible absolute top-[calc(50%-12px)] cursor-pointer group-hover:visible',
        { '-left-6': direction === HorizontalMoveDirection.LEFT },
        { '-right-6': direction === HorizontalMoveDirection.RIGHT },
        { hidden: disabled },
        // node is above the line
        {
          '-rotate-45': !showControlsInSingleRowState && direction === HorizontalMoveDirection.LEFT && index % 2 === 0,
        },
        {
          'rotate-45': !showControlsInSingleRowState && direction === HorizontalMoveDirection.RIGHT && index % 2 === 0,
        },
        // node is below the line
        {
          'rotate-45': !showControlsInSingleRowState && direction === HorizontalMoveDirection.LEFT && index % 2 === 1,
        },
        {
          '-rotate-45': !showControlsInSingleRowState && direction === HorizontalMoveDirection.RIGHT && index % 2 === 1,
        }
      )}
      onClick={handleClick}
    >
      {icon}
    </div>
  );
};

export default memo(HorizontalMoveControl);
